/**
 * What the Edit user page reads and writes beyond the People routes: a project's team roster (to get a person's
 * role, responsibilities and Active flag on that project), a team entry's changes, and the login's role and removal.
 *
 * The team routes are per project (`/planning/:project_id/team`), so these take the project explicitly: a person is
 * on several projects and the active one is not necessarily the one being edited. No new backend route is used.
 * Contract: alfa2267/ans-backend routes/planning/team.ts and users.ts.
 */
import { apiDelete, apiGet, apiPut, planningPath } from './PlanningApiClient';
import type { LinePatch } from '../components/people/personLines';
import type { UserRole } from '../types/User';
import type { TeamMember } from '../types/Team';

const projectTeam = (projectId: string, suffix = '') => `/planning/${encodeURIComponent(projectId)}/team${suffix}`;

/** The active team of one project. Admin and owner only (the API refuses a lower role). */
export async function fetchProjectRoster(projectId: string): Promise<TeamMember[]> {
  const res = await apiGet<{ members?: TeamMember[] } | null>(projectTeam(projectId, '?status=active'));
  return res?.members ?? [];
}

/** Sends only the fields of the patch. Empty role becomes null; responsibilities go as a list, one per line. */
export function updateMembership(projectId: string, teamMemberId: string, patch: LinePatch): Promise<TeamMember> {
  const body: Record<string, unknown> = { ...patch };
  if (typeof patch.responsibilities === 'string') {
    body.responsibilities = patch.responsibilities.split('\n').map(s => s.trim()).filter(Boolean);
  }
  return apiPut<TeamMember>(projectTeam(projectId, `/${encodeURIComponent(teamMemberId)}`), body);
}

/**
 * Owner only. The role covers the whole workspace. Moving someone away from viewer clears the financially-blind
 * flag with it, as the Logins list does; moving to viewer leaves the flag as it is.
 */
export function changeLoginRole(userId: string, role: UserRole): Promise<unknown> {
  const body: Record<string, unknown> = { role };
  if (role !== 'viewer') body.financially_blind = false;
  return apiPut<unknown>(planningPath(`/users/${encodeURIComponent(userId)}`), body);
}

/** Owner only. Removes the login (the person stays in the directory). */
export function removeLogin(userId: string): Promise<unknown> {
  return apiDelete<unknown>(planningPath(`/users/${encodeURIComponent(userId)}`));
}
